import React from 'react';
import { View, StyleSheet, StyleProp, ViewStyle, TouchableOpacity, Platform, useWindowDimensions } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Typography } from './Typography';
import { fonts } from '../../styles';
import { useTheme } from '../../context/ThemeContext';
import { fontWeights } from '../../styles/typographyTokens';

import { ACCENT_TEXT } from '../../styles/accentText';
export interface SectionHeaderProps {
    overline?: string;
    title: React.ReactNode;
    titleHighlight?: string;
    subtitle?: React.ReactNode;
    actionText?: string;
    onActionPress?: () => void;
    actionButton?: React.ReactNode;
    style?: StyleProp<ViewStyle>;
    /** Override the default "sectionTitle" variant for the title text */
    titleVariant?: 'sectionTitle' | 'communityHeader';
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
    overline,
    title,
    titleHighlight,
    subtitle,
    actionText,
    onActionPress,
    actionButton,
    style,
    titleVariant = 'sectionTitle',
}) => {
    const { isDark, colors } = useTheme();
    const { width } = useWindowDimensions();
    const isMobile = width < 768;

    return (
        <View style={[styles.container, !subtitle && styles.containerCompact, isMobile && styles.containerMobile, style]}>
            <View style={styles.textColumn}>
                {overline ? (
                    <Typography variant="overline" style={[styles.overline, { color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }]}>
                        {overline}
                    </Typography>
                ) : null}

                <Typography variant={titleVariant} style={[styles.title, { color: colors.textPrimary }]}>
                    {title}
                    {titleHighlight ? (
                        <>
                            {' '}
                            <Typography variant={titleVariant} style={styles.titleHighlight}>
                                {titleHighlight}
                            </Typography>
                        </>
                    ) : null}
                </Typography>

                {subtitle ? (
                    <Typography variant="subtitle" style={[styles.subtitle, { color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(74,69,63,0.5)' }]}>
                        {subtitle}
                    </Typography>
                ) : null}
            </View>

            {/* Action affordance («ВСЕ →» link / custom button) hidden on
                mobile — sections are horizontally swipeable carousels there,
                so the «see all» link adds noise without value. Desktop
                keeps the right-aligned action as before. */}
            {!isMobile && actionText && onActionPress ? (
                <View style={styles.actionColumn}>
                    <TouchableOpacity style={styles.standardActionBtn} onPress={onActionPress} activeOpacity={0.7}>
                        <Typography style={[styles.standardActionText, { color: colors.textPrimary }]} numberOfLines={1}>
                            {actionText.toUpperCase()}
                        </Typography>
                        <Ionicons name="arrow-forward" size={16} color={colors.textPrimary} style={{ opacity: 0.8 }} />
                    </TouchableOpacity>
                </View>
            ) : !isMobile && actionButton ? (
                <View style={styles.actionColumn}>
                    {actionButton}
                </View>
            ) : null}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: 56,
        flexWrap: 'wrap',
        gap: 16,
    },
    containerCompact: {
        marginBottom: 28,
    },
    containerMobile: {
        flexDirection: 'column',
        gap: 12,
        marginBottom: 24,
    },
    textColumn: {
        flex: 1,
        minWidth: Platform.OS === 'web' ? 280 : 0,
    },
    actionColumn: {
        alignSelf: 'flex-end',
    },
    overline: {
        marginBottom: 16,
        letterSpacing: 2,
    },
    title: {
        marginBottom: 0,
    },
    titleHighlight: {
        color: ACCENT_TEXT,
    },
    subtitle: {
        marginTop: 16,
        maxWidth: 800,
    },
    standardActionBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingVertical: 8,
    },
    standardActionText: {
        fontSize: 13,
        letterSpacing: 1,
        opacity: 0.8,
        // Inter в бандле не было — рисовался системный. Вес на нативе задаёт
        // файл семейства (fonts.heading = SemiBold), web синтезирует 800 сам.
        fontFamily: fonts.heading,
        ...(Platform.OS === 'web' ? { fontWeight: fontWeights.bold } : null),
    }
});
